// src/components/Beats.tsx
import { useEffect, useRef, useCallback } from "react";
import { gsap } from "gsap";

interface BeatsProps {
  isRunning: boolean;
  currentBeat: number;
  currentSubBeat: number;
  stressFirstBeat: boolean;
  stressedBeats: number[];
  onBeatClick: (beatNumber: number) => void;
  beatsPerBar: number;
  customSongStructureOn?: boolean; // Disable beat clicking when custom song structure is enabled
  beatNumbersEnabled?: boolean;
}

export const Beats = ({
  isRunning,
  currentBeat,
  currentSubBeat,
  stressFirstBeat,
  stressedBeats,
  onBeatClick,
  beatsPerBar,
  customSongStructureOn = false,
  beatNumbersEnabled = false,
}: BeatsProps) => {
  const dotRefs = useRef<SVGCircleElement[]>([]);
  const stressRingRefs = useRef<SVGCircleElement[]>([]);
  const numberRefs = useRef<SVGTextElement[]>([]);

  const animateDot = useCallback(
    (beat: number) => {
      if (!isRunning) return;

      const index = beat - 1;
      const dot = dotRefs.current[index];
      const stressRing = stressRingRefs.current[index];
      const number = numberRefs.current[index];

      if (dot) {
        const isStressed = stressedBeats.includes(beat);
        const pulseColor = "var(--beat-dot-active)";

        // Animate color change (permanent until bar resets)
        gsap.fromTo(
          dot,
          {
            fill: "var(--beat-dot-inactive)", // Use CSS variable
          },
          {
            fill: pulseColor,
            duration: 0.1,
            ease: "sine.inOut",
          }
        );

        // Animate number color to match the beat dot
        if (number) {
          gsap.fromTo(
            number,
            {
              fill: "var(--beat-dot-inactive)",
            },
            {
              fill: pulseColor,
              duration: 0.1,
              ease: "sine.inOut",
            }
          );
        }

        // Animate scale (pulse effect)
        gsap.fromTo(
          dot,
          {
            scale: 1,
            transformOrigin: "50% 50%",
          },
          {
            scale: 1.2,
            duration: 0.075,
            yoyo: true,
            repeat: 1,
            ease: "sine.inOut",
          }
        );

        // Animate stress ring if it exists and this is a stressed beat
        if (stressRing && isStressed && stressFirstBeat) {
          // Animate stress ring color change (permanent until bar resets)
          gsap.fromTo(
            stressRing,
            {
              stroke: "var(--beat-dot-inactive)", // Use CSS variable
            },
            {
              stroke: pulseColor,
              duration: 0.1,
              ease: "sine.inOut",
            }
          );

          // Animate stress ring scale (pulse effect)
          gsap.fromTo(
            stressRing,
            {
              scale: 1,
              transformOrigin: "50% 50%",
            },
            {
              scale: 1.2,
              duration: 0.075,
              yoyo: true,
              repeat: 1,
              ease: "sine.inOut",
            }
          );
        }
      }
    },
    [isRunning, stressFirstBeat, stressedBeats]
  );

  const resetDotColors = () => {
    dotRefs.current.forEach((dot) => {
      if (dot) {
        gsap.killTweensOf(dot);
        gsap.set(dot, {
          fill: "var(--beat-dot-inactive)", // Use CSS variable
          scale: 1,
          transformOrigin: "50% 50%",
        });
      }
    });

    stressRingRefs.current.forEach((stressRing) => {
      if (stressRing) {
        gsap.killTweensOf(stressRing);
        gsap.set(stressRing, {
          stroke: "var(--beat-dot-inactive)", // Use CSS variable
          scale: 1,
          transformOrigin: "50% 50%",
        });
      }
    });

    numberRefs.current.forEach((number) => {
      if (number) {
        gsap.killTweensOf(number);
        gsap.set(number, {
          fill: "var(--beat-dot-inactive)",
        });
      }
    });
  };

  useEffect(() => {
    if (isRunning && currentBeat > 0 && currentSubBeat === 1) {
      animateDot(currentBeat);
    }
    // Remove the else clause that was calling resetDotColors
  }, [currentBeat, currentSubBeat, isRunning, animateDot]);

  // Reset all dots to gray at the start of each bar
  useEffect(() => {
    const handleBar = () => {
      resetDotColors();
    };
    // Cast event name to any to avoid TS complaining about custom events on Window
    window.addEventListener("bar" as any, handleBar as EventListener);
    return () => {
      window.removeEventListener("bar" as any, handleBar as EventListener);
    };
  }, []);

  // Ensure dots reset when playback stops
  useEffect(() => {
    if (!isRunning) {
      resetDotColors();
    }
  }, [isRunning]);

  // Calculate layout for multiple rows if needed
  const maxBeatsPerRow = 8; // Same for both small and large screens
  const numRows = Math.ceil(beatsPerBar / maxBeatsPerRow);
  const beatsInFirstRow = Math.min(beatsPerBar, maxBeatsPerRow);
  const beatsInSecondRow =
    beatsPerBar > maxBeatsPerRow ? beatsPerBar - maxBeatsPerRow : 0;

  // SVG dimensions - consistent for all screen sizes
  // Extra height/spacing when beat numbers are shown so labels aren't clipped
  // and second-row numbers clear the first-row dots
  const svgWidth = 500;
  const svgHeight =
    numRows === 1
      ? beatNumbersEnabled
        ? 130
        : 100
      : beatNumbersEnabled
        ? 220
        : 160;
  const firstRowCy = beatNumbersEnabled ? 68 : 50;
  const secondRowCy = beatNumbersEnabled ? 160 : 110;
  const numberOffsetY = 38;

  // Calculate spacing for each row - consistent for all screen sizes
  const getSpacing = (beatsInRow: number) => {
    if (beatsInRow <= 1) return 0;
    const maxSpacing = 100;
    const availableWidth = 400;
    return Math.min(maxSpacing, availableWidth / (beatsInRow - 1));
  };

  const firstRowSpacing = getSpacing(beatsInFirstRow);
  // Use the same spacing as the first row to ensure proper alignment
  const secondRowSpacing = firstRowSpacing;

  const getOffsetX = (beatsInRow: number, spacing: number) => {
    if (beatsInRow <= 1) return svgWidth / 2; // Center single beat
    const totalWidth = (beatsInRow - 1) * spacing;
    return (svgWidth - totalWidth) / 2;
  };

  const firstRowOffsetX = getOffsetX(beatsInFirstRow, firstRowSpacing);
  // For second row, align with first row instead of centering independently
  // This ensures beat 9 aligns with beat 1, beat 10 with beat 2, etc.
  const secondRowOffsetX = firstRowOffsetX;

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <svg
        width={svgWidth}
        height={svgHeight}
        style={{
          display: "block",
          transition: "height 0.3s ease-in-out, width 0.3s ease-in-out",
        }}
      >
        <defs>
          <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="3" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* First row */}
        {Array.from({ length: beatsInFirstRow }, (_, i) => (
          <g key={i}>
            {beatNumbersEnabled && (
              <text
                ref={(el) => {
                  if (el) numberRefs.current[i] = el;
                }}
                x={firstRowOffsetX + i * firstRowSpacing}
                y={firstRowCy - numberOffsetY}
                textAnchor="middle"
                dominantBaseline="auto"
                fill="var(--beat-dot-inactive)"
                fontSize="14"
                fontWeight={
                  stressFirstBeat && stressedBeats.includes(i + 1)
                    ? "700"
                    : "500"
                }
                style={{ pointerEvents: "none", userSelect: "none" }}
              >
                {i + 1}
              </text>
            )}
            {/* Stress ring - only visible on stressed beats when stressFirstBeat is enabled */}
            {stressFirstBeat && stressedBeats.includes(i + 1) && (
              <circle
                ref={(el) => {
                  if (el) stressRingRefs.current[i] = el;
                }}
                cx={firstRowOffsetX + i * firstRowSpacing}
                cy={firstRowCy}
                r={22}
                fill="none"
                stroke="var(--beat-dot-inactive)"
                strokeWidth="2"
                filter="url(#glow)"
              />
            )}
            {/* Beat dot */}
            <circle
              ref={(el) => {
                if (el) dotRefs.current[i] = el;
              }}
              cx={firstRowOffsetX + i * firstRowSpacing}
              cy={firstRowCy}
              r={15}
              fill="var(--beat-dot-inactive)" // Use CSS variable
              filter="url(#glow)"
              tabIndex={-1}
              style={{
                cursor:
                  stressFirstBeat && !customSongStructureOn
                    ? "pointer"
                    : "default",
                outline: "none",
                WebkitTapHighlightColor: "transparent",
              }}
              onClick={() => {
                // Disable clicking when custom song structure is enabled
                if (!customSongStructureOn) {
                  // Add pulse animation on click
                  const dot = dotRefs.current[i];
                  if (dot) {
                    gsap.fromTo(
                      dot,
                      { scale: 1 },
                      {
                        scale: 1.15,
                        duration: 0.1,
                        yoyo: true,
                        repeat: 1,
                        ease: "power2.inOut",
                      }
                    );
                  }
                  onBeatClick(i + 1);
                }
              }}
            />
          </g>
        ))}

        {/* Second row (if needed) */}
        {beatsInSecondRow > 0 &&
          Array.from({ length: beatsInSecondRow }, (_, i) => (
            <g key={i + beatsInFirstRow}>
              {beatNumbersEnabled && (
                <text
                  ref={(el) => {
                    if (el) numberRefs.current[i + beatsInFirstRow] = el;
                  }}
                  x={secondRowOffsetX + i * secondRowSpacing}
                  y={secondRowCy - numberOffsetY}
                  textAnchor="middle"
                  dominantBaseline="auto"
                  fill="var(--beat-dot-inactive)"
                  fontSize="14"
                  fontWeight={
                    stressFirstBeat &&
                    stressedBeats.includes(i + beatsInFirstRow + 1)
                      ? "800"
                      : "500"
                  }
                  style={{ pointerEvents: "none", userSelect: "none" }}
                >
                  {i + beatsInFirstRow + 1}
                </text>
              )}
              {/* Stress ring - only visible on stressed beats when stressFirstBeat is enabled */}
              {stressFirstBeat &&
                stressedBeats.includes(i + beatsInFirstRow + 1) && (
                  <circle
                    ref={(el) => {
                      if (el) stressRingRefs.current[i + beatsInFirstRow] = el;
                    }}
                    cx={secondRowOffsetX + i * secondRowSpacing}
                    cy={secondRowCy}
                    r={22}
                    fill="none"
                    stroke="var(--beat-dot-inactive)"
                    strokeWidth="2"
                    filter="url(#glow)"
                  />
                )}
              {/* Beat dot */}
              <circle
                ref={(el) => {
                  if (el) dotRefs.current[i + beatsInFirstRow] = el;
                }}
                cx={secondRowOffsetX + i * secondRowSpacing}
                cy={secondRowCy}
                r={15}
                fill="var(--beat-dot-inactive)" // Use CSS variable
                filter="url(#glow)"
                tabIndex={-1}
                style={{
                  cursor:
                    stressFirstBeat && !customSongStructureOn
                      ? "pointer"
                      : "default",
                  outline: "none",
                  WebkitTapHighlightColor: "transparent",
                }}
                onClick={() => {
                  // Disable clicking when custom song structure is enabled
                  if (!customSongStructureOn) {
                    // Add pulse animation on click
                    const dot = dotRefs.current[i + beatsInFirstRow];
                    if (dot) {
                      gsap.fromTo(
                        dot,
                        { scale: 1 },
                        {
                          scale: 1.15,
                          duration: 0.1,
                          yoyo: true,
                          repeat: 1,
                          ease: "power2.inOut",
                        }
                      );
                    }
                    onBeatClick(i + beatsInFirstRow + 1);
                  }
                }}
              />
            </g>
          ))}
      </svg>
    </div>
  );
};
